import {
  applyCoachProfileChanges,
  COACH_PROFILE_SECTIONS,
  type CoachProfileChange,
  CoachProfileEditor,
  CoachProfileSectionNav,
  coachProfileSectionSpy,
  type CoachProfileValidationFailure,
  diffCoachProfiles,
  markCoachProfileDirty,
  pruneEmptyCoachProfileFragments,
  validateCoachProfile,
} from '@efdigital/ll-addon-coaching';
import type PicklistsService from '@efdigital/ll-addon-content/services/picklists';
import type { CoachProfile } from '@efdigital/ll-addon-data';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import {
  COACHING_COUNTRIES_GROUP,
  COACHING_INDUSTRIES_GROUP,
  COACHING_SENIORITY_GROUP,
  COACHING_SPECIALISMS_GROUP,
  COACHING_TYPES_GROUP,
  createCoachProfile,
  updateCoachProfile,
} from '@efdigital/ll-xceed-v2/data/builders/coaching/coach-profiles';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

import CoachProfileView from './coach-profile.gts';

interface EditMyProfileSignature {
  Args: {
    /** The coach's live, published profile. */
    published: CoachProfile;
    /** Their in-flight change request awaiting review, if any. */
    pending: CoachProfile | null;
    /**
     * Called after a successful submit, so the host can react (e.g. refresh
     * the route). Keeps this component from needing to know about routing.
     */
    onSaved?: () => void | Promise<void>;
  };
  Element: HTMLDivElement;
}

/**
 * A coach's self-service view of their own profile. Read-only by default;
 * "Edit" opens the shared CoachProfileEditor in `self` mode against a scratch
 * draft. Saving submits a `pending_review` change request (a new record, or
 * an update to an existing pending one) — the published profile is never
 * touched directly, so the edit lands in the admin approval queue rather than
 * going live immediately.
 */
export default class EditMyProfile extends Component<EditMyProfileSignature> {
  @service declare store: Store;
  @service declare picklists: PicklistsService;

  @tracked mode: 'view' | 'edit' = 'view';
  /** Scratch record edited in the form; copied onto/into a saved record. */
  @tracked workingCopy: CoachProfile | null = null;
  @tracked saving = false;
  @tracked saveError: string | null = null;
  @tracked validationFailures: CoachProfileValidationFailure[] = [];
  @tracked activeSection = COACH_PROFILE_SECTIONS[0]?.id ?? 'identity';

  // Set while a click-triggered smooth-scroll is in flight, so the section
  // spy doesn't fight it and flicker through intermediate sections.
  #suppressSpyUntil = 0;
  #resumeSpyTimeout: ReturnType<typeof setTimeout> | null = null;

  // Picklist options come from the picklists service (the public, cached
  // /end-user/picklists/group-items endpoint) — no read:picklists right needed.
  get specialtyOptions() {
    return this.picklists.groups[COACHING_SPECIALISMS_GROUP]?.items ?? [];
  }

  // Locations stay free text (omitted) — the editor falls back to tag entry.
  get positioningOptions() {
    return {
      countries: this.picklists.groups[COACHING_COUNTRIES_GROUP]?.items ?? [],
      coachingTypes: this.picklists.groups[COACHING_TYPES_GROUP]?.items ?? [],
      industries: this.picklists.groups[COACHING_INDUSTRIES_GROUP]?.items ?? [],
      levelsOfSeniority:
        this.picklists.groups[COACHING_SENIORITY_GROUP]?.items ?? [],
    };
  }

  get isEditing(): boolean {
    return this.mode === 'edit';
  }

  get hasPending(): boolean {
    return Boolean(this.args.pending);
  }

  /** What the coach has changed in their awaiting-review submission. */
  get pendingChanges(): CoachProfileChange[] {
    return this.args.pending
      ? diffCoachProfiles(this.args.published, this.args.pending)
      : [];
  }

  get editLabel(): string {
    return this.hasPending ? 'Continue editing changes' : 'Edit profile';
  }

  startEdit = () => {
    const draft = this.store.createRecord<CoachProfile>(
      DataTypeCoaching.CoachProfile,
      {
        user: this.args.published.user,
        status: 'pending_review',
      }
    );

    // Seed from the pending submission if one exists, else the published
    // profile — applyCoachProfileChanges deep-clones arrays and fragments, so
    // the draft never aliases the source record.
    applyCoachProfileChanges(draft, this.args.pending ?? this.args.published);

    this.workingCopy = draft;
    this.validationFailures = [];
    this.saveError = null;
    this.activeSection = COACH_PROFILE_SECTIONS[0]?.id ?? 'identity';
    this.mode = 'edit';
  };

  cancelEdit = () => {
    // Rolling back a never-saved createRecord unloads it from the store.
    this.workingCopy?.rollbackAttributes();
    this.workingCopy = null;
    this.validationFailures = [];
    this.saveError = null;
    this.mode = 'view';
  };

  submitChanges = async () => {
    const draft = this.workingCopy;

    if (!draft || this.saving) {
      return;
    }

    this.saveError = null;
    pruneEmptyCoachProfileFragments(draft);

    const failures = validateCoachProfile(draft, { mode: 'self' });

    if (failures.length > 0) {
      this.validationFailures = failures;
      this.scrollToSectionId(failures[0]!.sectionId);
      return;
    }

    this.validationFailures = [];
    this.saving = true;

    try {
      const pending = this.args.pending;

      if (pending) {
        // Fold the draft onto the existing change request and update it.
        applyCoachProfileChanges(pending, draft);
        markCoachProfileDirty(pending);
        try {
          await this.store.request(updateCoachProfile(pending, this.store));
        } catch (error) {
          // Revert the in-place mutation so a failed save doesn't leave the
          // pending record dirty / showing changes that never persisted.
          pending.rollbackAttributes();
          throw error;
        }
        draft.rollbackAttributes();
      } else {
        // First submission: the draft itself becomes the pending record.
        markCoachProfileDirty(draft);
        await this.store.request(createCoachProfile(draft, this.store));
      }

      this.workingCopy = null;
      this.mode = 'view';
      await this.args.onSaved?.();
    } catch (error: unknown) {
      this.saveError =
        error instanceof Error
          ? error.message
          : 'Something went wrong submitting your changes.';
    } finally {
      this.saving = false;
    }
  };

  scrollToSection = (sectionId: string, event: Event) => {
    event.preventDefault();
    this.#pauseSpyDuringScroll();
    this.scrollToSectionId(sectionId);
  };

  scrollToSectionId = (sectionId: string) => {
    this.activeSection = sectionId;
    document
      .getElementById(`coach-section-${sectionId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Highlight the section the spy reports, unless a click-triggered
  // smooth-scroll is mid-flight (then keep the clicked target highlighted).
  handleSpyActiveSection = (sectionId: string) => {
    if (Date.now() < this.#suppressSpyUntil) {
      return;
    }

    this.activeSection = sectionId;
  };

  /**
   * Suppresses the auto-highlight while a click-triggered smooth-scroll is in
   * flight, so it doesn't flicker through the sections it passes on the way.
   * Resumes on the native `scrollend` event where supported, with a flat
   * timeout fallback.
   */
  #pauseSpyDuringScroll = () => {
    this.#suppressSpyUntil = Date.now() + 800;

    if (this.#resumeSpyTimeout) {
      clearTimeout(this.#resumeSpyTimeout);
    }

    this.#resumeSpyTimeout = setTimeout(() => {
      this.#suppressSpyUntil = 0;
    }, 800);

    if ('onscrollend' in window) {
      window.addEventListener(
        'scrollend',
        () => {
          this.#suppressSpyUntil = 0;

          if (this.#resumeSpyTimeout) {
            clearTimeout(this.#resumeSpyTimeout);
            this.#resumeSpyTimeout = null;
          }
        },
        { once: true }
      );
    }
  };

  <template>
    <div ...attributes>
      {{#if this.isEditing}}
        <div class="row g-4 gx-lg-4">
          <aside class="col-12 col-lg-4">
            {{! sticky lives on a wrapper: .card sets position:relative and
                wins over .sticky-lg-top in this theme's import order }}
            <div
              class="ll-coach-profile-sticky-nav sticky-lg-top py-lg-3 overflow-y-auto"
            >
              <div class="card border shadow-sm rounded-4 p-3 p-lg-4">
                <button
                  type="button"
                  class="btn btn-link p-0 mb-2 small text-body-secondary text-decoration-none d-inline-flex align-items-center gap-1"
                  disabled={{this.saving}}
                  {{on "click" this.cancelEdit}}
                >
                  <iconify-icon
                    inline
                    icon="ph:caret-left"
                    aria-hidden="true"
                  />
                  Back to profile
                </button>
                <h2 class="h5 fw-bold mb-2">Edit your profile</h2>

                <hr class="my-2" />

                <CoachProfileSectionNav
                  @activeSection={{this.activeSection}}
                  @onSelect={{this.scrollToSection}}
                />

                <hr class="my-2" />

                {{#if this.saveError}}
                  <div class="alert alert-danger small p-2" role="alert">
                    {{this.saveError}}
                  </div>
                {{/if}}

                {{#if this.validationFailures.length}}
                  <div class="alert alert-danger small p-2" role="alert">
                    {{#each this.validationFailures as |failure|}}
                      <p class="mb-1">{{failure.message}}</p>
                    {{/each}}
                  </div>
                {{/if}}

                <div class="d-flex gap-2 justify-content-center">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    disabled={{this.saving}}
                    {{on "click" this.cancelEdit}}
                  >Cancel</button>
                  <button
                    type="button"
                    class="btn btn-sm btn-primary text-nowrap"
                    disabled={{this.saving}}
                    aria-busy={{this.saving}}
                    {{on "click" this.submitChanges}}
                  >
                    {{#if this.saving}}
                      <span
                        class="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Submitting…
                    {{else}}
                      Submit
                    {{/if}}
                  </button>
                </div>
              </div>
            </div>
          </aside>

          <div class="col-12 col-lg-8">
            {{#if this.workingCopy}}
              <div {{coachProfileSectionSpy this.handleSpyActiveSection}}>
                <CoachProfileEditor
                  @coach={{this.workingCopy}}
                  @mode="self"
                  @specialtyOptions={{this.specialtyOptions}}
                  @positioningOptions={{this.positioningOptions}}
                />
              </div>
            {{/if}}
          </div>
        </div>
      {{else}}
        {{#if this.hasPending}}
          <div class="alert alert-info d-flex flex-column gap-2" role="status">
            <div class="d-flex align-items-start gap-2">
              <iconify-icon
                inline
                icon="ph:clock-countdown"
                class="fs-5 flex-shrink-0"
                aria-hidden="true"
              />
              <div>
                <p class="fw-semibold mb-1">Your changes are awaiting review.</p>
                <p class="small mb-0">
                  You'll keep seeing your current live profile below until an
                  administrator approves your requested changes.
                </p>
              </div>
            </div>
            {{#if this.pendingChanges.length}}
              <div class="small">
                Pending updates:
                {{#each this.pendingChanges as |change index|}}
                  {{if index ", "}}<span
                    class="fw-semibold"
                  >{{change.label}}</span>
                {{/each}}
              </div>
            {{/if}}
          </div>
        {{/if}}

        <CoachProfileView @coach={{@published}}>
          <:actions>
            <button
              type="button"
              class="btn btn-primary d-inline-flex align-items-center gap-2 mt-3"
              {{on "click" this.startEdit}}
            >
              <iconify-icon inline icon="ph:pencil-simple" aria-hidden="true" />
              {{this.editLabel}}
            </button>
          </:actions>
        </CoachProfileView>
      {{/if}}
    </div>
  </template>
}
